import { PrismaticJoint } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, floor, walls, box, rng } from "./helpers";

/**
 * Prismatic (slider) joints as powered elevators. Each platform is locked to a
 * vertical axis and driven by a linear motor that reverses at its travel
 * limits, ferrying a load of crates up and down. Grab a crate mid-ride.
 */
export const elevators: Demo = {
	id: "elevators",
	title: "Elevators",
	blurb:
		"Prismatic joints with linear motors. Each platform slides on a vertical axis between travel limits, reversing at each end to ferry a stack of crates up and down.",
	category: "Joints & Machines",
	build(): Scene {
		const world = makeWorld();
		floor(world, 26);
		walls(world, 11, 18);

		const rand = rng(99);
		const lifts: { joint: PrismaticJoint; up: number; lo: number; hi: number }[] = [];
		const columns = [-6, 0, 6];
		for (const cx of columns) {
			// A static guide post the platform indexes against.
			const post = box(world, cx, -1, 0.3, 0.3, { isStatic: true });
			// The platform starts low and rides up.
			const platform = box(world, cx, -1, 3, 0.4, { density: 4, friction: 0.9 });
			const lo = 0; // starts here (translation 0)
			const hi = 7; // +axis points up, so rising increases translation
			const joint = new PrismaticJoint(post, platform, {
				localAnchorA: [0, 0],
				localAnchorB: [0, 0],
				localAxisA: [0, -1], // +axis points up the screen
				enableMotor: true,
				motorSpeed: 2.5,
				maxMotorForce: 6000,
				enableLimit: true,
				lowerTranslation: lo,
				upperTranslation: hi,
			});
			world.addConstraint(joint);
			lifts.push({ joint, up: 1, lo, hi });

			// A few crates riding on the platform.
			const crates = 2 + Math.floor(rand() * 2);
			for (let i = 0; i < crates; i++) {
				box(world, cx + (rand() - 0.5), -1.6 - i * 0.9, 0.8, 0.8, {
					density: 0.6,
					friction: 0.8,
				});
			}
		}

		return {
			world,
			camera: new Camera(0, -5, 22),
			hint: "Powered sliders ferry the crates — grab one mid-ride",
			onBeforeStep(): void {
				// Reverse each motor as it nears a travel limit.
				for (const lift of lifts) {
					const t = lift.joint.translation;
					if (lift.up > 0 && t >= lift.hi - 0.15) lift.up = -1;
					else if (lift.up < 0 && t <= lift.lo + 0.15) lift.up = 1;
					lift.joint.motorSpeed = 2.5 * lift.up;
				}
			},
		};
	},
};
